import React from 'react';
import { ArrowRightLeft, XCircle, AlertCircle, Clock, Inbox } from 'lucide-react';
import { Loan } from '../types';
interface EscalationTableProps {
  loans: Loan[];
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onRowClick: (loan: Loan) => void;
  onMoveToRetention: (e: React.MouseEvent, loanId: string) => void;
  onCloseEscalation: (e: React.MouseEvent, loanId: string) => void;
}
export function EscalationTable({
  loans,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onRowClick,
  onMoveToRetention,
  onCloseEscalation
}: EscalationTableProps) {
  const allSelected = loans.length > 0 && selectedIds.length === loans.length;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const getDaysDifference = (dateStr: string) => {
    const diffTime = new Date(dateStr).getTime() - new Date().getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const getDateColorClass = (days: number) => {
    if (days < 0) return 'text-red-700 bg-red-50 ring-red-600/20';
    if (days <= 3) return 'text-red-700 bg-red-50 ring-red-600/20';
    if (days <= 7) return 'text-yellow-700 bg-yellow-50 ring-yellow-600/20';
    return 'text-gray-600 bg-gray-50 ring-gray-500/10';
  };
  return <div data-id="element-81">
      <div className="flex items-center justify-between mb-3" data-id="element-82">
        <p className="text-sm text-gray-500" data-id="element-83">
          Showing{' '}
          <span className="font-medium text-gray-700" data-id="element-84">{loans.length}</span>{' '}
          escalation{loans.length !== 1 ? 's' : ''}
          {selectedIds.length > 0 && <span className="ml-2 text-blue-600 font-medium" data-id="element-85">
              · {selectedIds.length} selected
            </span>}
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm" data-id="element-86">
        <div className="overflow-x-auto rounded-xl" data-id="element-87">
          <table className="w-full text-left border-collapse" data-id="element-88">
            <thead data-id="element-89">
              <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500 font-semibold" data-id="element-90">
                <th className="px-4 py-3 w-12 text-center" data-id="element-91">
                  <input type="checkbox" checked={allSelected} ref={input => {
                  if (input) input.indeterminate = someSelected;
                }} onChange={onToggleSelectAll} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer" data-id="element-92" />
                </th>
                <th className="px-4 py-3" data-id="element-93">Borrower / Loan</th>
                <th className="px-4 py-3" data-id="element-94">Closing Date</th>
                <th className="px-4 py-3" data-id="element-95">Team</th>
                <th className="px-4 py-3 text-right" data-id="element-96">Amount</th>
                <th className="px-4 py-3" data-id="element-97">Lock Exp.</th>
                <th className="px-4 py-3 text-right" data-id="element-98">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-99">
              {loans.length === 0 ? <tr data-id="element-100">
                  <td colSpan={7} className="px-4 py-16 text-center" data-id="element-101">
                    <div className="flex flex-col items-center gap-3" data-id="element-102">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-103">
                        <Inbox className="w-6 h-6 text-gray-400" data-id="element-104" />
                      </div>
                      <div data-id="element-105">
                        <p className="text-sm font-medium text-gray-900" data-id="element-106">
                          No escalations found
                        </p>
                        <p className="text-sm text-gray-500 mt-0.5" data-id="element-107">
                          Try adjusting your search or filters.
                        </p>
                      </div>
                    </div>
                  </td>
                </tr> : loans.map(loan => {
              const isSelected = selectedIds.includes(loan.id);
              const closingDays = getDaysDifference(loan.closingDate);
              const lockDays = getDaysDifference(loan.lockExpiration);
              return <tr key={loan.id} onClick={() => onRowClick(loan)} className={`group cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/40 hover:bg-blue-50/60' : 'bg-white hover:bg-gray-50'}`} data-id="element-108">
                      <td className="px-4 py-3.5 text-center" onClick={e => e.stopPropagation()} data-id="element-109">
                        <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(loan.id)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer" data-id="element-110" />
                      </td>
                      <td className="px-4 py-3.5" data-id="element-111">
                        <div className="flex items-center gap-2.5" data-id="element-112">
                          {loan.urgency === 'high' && <span className="flex-shrink-0 w-2 h-2 rounded-full bg-red-500" data-id="element-113"></span>}
                          {loan.urgency === 'medium' && <span className="flex-shrink-0 w-2 h-2 rounded-full bg-amber-400" data-id="element-114"></span>}
                          {loan.urgency === 'low' && <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-300" data-id="element-115"></span>}
                          <div data-id="element-116">
                            <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors text-sm" data-id="element-117">
                              {loan.borrowerName}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-118">
                              {loan.loanName} · #{loan.loanId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5" data-id="element-119">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ring-1 ring-inset ${getDateColorClass(closingDays)}`} data-id="element-120">
                          {formatDate(loan.closingDate)}
                          {closingDays <= 3 && closingDays >= 0 && <Clock className="w-3 h-3" data-id="element-121" />}
                        </span>
                      </td>
                      <td className="px-4 py-3.5" data-id="element-122">
                        <div className="text-sm text-gray-900" data-id="element-123">
                          {loan.processor}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5" data-id="element-124">
                          LOA: {loan.loa}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-right tabular-nums" data-id="element-125">
                        <span className="text-sm font-medium text-gray-900" data-id="element-126">
                          {formatCurrency(loan.loanAmount)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5" data-id="element-127">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ring-1 ring-inset ${getDateColorClass(lockDays)}`} data-id="element-128">
                          {formatDate(loan.lockExpiration)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()} data-id="element-129">
                        <div className="flex items-center justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-130">
                          <button onClick={e => onMoveToRetention(e, loan.id)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700 hover:text-blue-700 rounded-lg text-xs font-medium transition-all" data-id="element-131">
                            <ArrowRightLeft className="w-3 h-3" data-id="element-132" />
                            Retention
                          </button>
                          <button onClick={e => onCloseEscalation(e, loan.id)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 hover:border-red-300 hover:bg-red-50 text-gray-700 hover:text-red-600 rounded-lg text-xs font-medium transition-all" data-id="element-133">
                            <XCircle className="w-3 h-3" data-id="element-134" />
                            Close
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}